import React, { useState, useEffect } from "react";
import {
  Paper,
  Grid,
  makeStyles,
  Typography,
  Button,
  Tabs,
  Tab,
  Divider,
  FormControl,
  FormGroup,
  FormControlLabel,
  Checkbox,
} from "@material-ui/core";
import useTable from "../../../hooks/table/useTable";
import useInput from "../../../hooks/input/useInput";
import TrTable from "../../../components/shared/controls/TrTable";
import TrSelect from "../../shared/controls/TrSelect";
import TrTableHeadMetadata from "../../../models/shared/TrTableHeadMetadata";
import TrTableBodyData from "../../../models/shared/TrTableBodyData";
import TrSelectData from "../../../models/shared/TrSelectData";
import YearFilter from "../../../models/shared/YearFilter";
import MonthFilter from "../../../models/shared/MonthFilter";
import PaymentListFilter from "../../../models/admin/payment/PaymentListFilter";
import PaymentListFilterValue from "../../../models/admin/payment/PaymentListFilterValue";
import PaymentListPagedResult from "../../../models/admin/payment/PaymentListPagedResult";
import PaymentList from "../../../models/admin/payment/PaymentList";
import SchoolType from "../../../models/school-type/SchoolTypeEnum";
import PaymentApiClient from "../../../api/admin/PaymentApiClient";
import PaymentGroupApiClient from "../../../api/admin/PaymentGroupApiClient";
import { Done as DoneIcon } from "@material-ui/icons";
import {
  nameof,
  numberFormat,
  currencyFormat,
} from "../../../utilities/utilities";
import PaymentRequestStatusApiClient from "../../../api/PaymentRequestStatusApiClient";
import TrSelectPagedList from "../../../models/shared/TrSelectPagedList";
import Drawer from "@material-ui/core/Drawer";
import ButtonConfirmDialog from "../../shared/common/ButtonConfirmDialog";
import useSnackbar from "../../../hooks/snackbar/useSnackbar";
import TrSnackbar from "../../shared/controls/TrSnackbar";
import PaymentGroup from "../../../models/admin/payment-group/PaymentGroup";
import PaymentRequestStatus from "../../../models/payment-request-status/PaymentRequestStatusEnum";
import PaymentRequestStatusElement from "../../shared/controls/PaymentRequestStatusElement";

export default function PaymentListPage() {
  const classes = useStyles({});

  const [tabIndex, setTabIndex] = useState(0);
  const [filters, setFilters] = useState<PaymentListFilter>(
    {} as PaymentListFilter
  );
  const [filterValue, setFilterValue] = useState<PaymentListFilterValue>({
    schoolTypeId: 0,
    schoolYearId: 0,
    date: undefined,
    paymentGroupIds: new Array<number>(),
  } as PaymentListFilterValue);
  const [paymentListPaged, setPaymentListPaged] = useState<
    PaymentListPagedResult
  >({} as PaymentListPagedResult);

  const [groups, setGroups] = useState<Array<PaymentGroup>>(
    new Array<PaymentGroup>()
  );

  const [paymentGroup, setPaymentGroup] = useState<string>("");

  const {
    page,
    pageSize,
    handleChangePage,
    handleChangepageSize,
    handleTableSelectChange,
    handleTableAllSelectChange,
    getAllSelectedListItems,
    allItemsSelected,
    getAllSelectedLength,
    handleResetDataList,
  } = useTable(paymentListPaged.pagedTableItems);

  const { handleSelectChange } = useInput();

  const {
    isOpened: isSnackbarOpened,
    open: openSnackbar,
    close: closeSnackbar,
    message: snackbarMessage,
    variant: snackbarVariant,
  } = useSnackbar();

  useEffect(() => {
    getFilters();
  }, []);

  useEffect(() => {
    setFilterValue((previousFilterValue) => ({
      ...previousFilterValue,
      schoolTypeId: resolveTabSchoolType(),
    }));
  }, [tabIndex]);

  useEffect(() => {
    if (filterValue.schoolYearId) {
      search();
    }
  }, [filterValue, page, pageSize]);

  useEffect(() => {
    if (filterValue.schoolYearId && filterValue.date) {
      handleGetGroups(filterValue.schoolTypeId, filterValue.date! as Date);
    }

    setFilterValue({ ...filterValue, paymentGroupIds: new Array<number>() });
  }, [filterValue.schoolTypeId, filterValue.schoolYearId, filterValue.date]);

  async function getFilters() {
    const apiClient = new PaymentApiClient();
    const searchFilters = await apiClient.getSearchFilters();

    setFilterValue((previousState) => {
      const selectedSchoolYear =
        searchFilters.schoolYears.length > 0
          ? searchFilters.schoolYears[0]
          : "";

      let selectedDate: Date | string = "";
      if (
        selectedSchoolYear &&
        selectedSchoolYear.months &&
        selectedSchoolYear.months.length > 0
      ) {
        const currentMonth = selectedSchoolYear.months.find((m) => m.isCurrent);
        selectedDate =
          (currentMonth && currentMonth.startingDate) ||
          selectedSchoolYear.months.slice(-1)[0].startingDate;
      }

      return {
        ...previousState,
        schoolTypeId: resolveTabSchoolType(),
        schoolYearId: selectedSchoolYear && selectedSchoolYear.id,
        date: selectedDate,
      };
    });

    setFilters(searchFilters);
  }

  async function handleGetGroups(schoolTypeId: number, data: Date) {
    const apiClient = new PaymentGroupApiClient();
    const groupsRespond = await apiClient.getAllByTypeAndDate(
      schoolTypeId,
      data
    );

    setGroups(groupsRespond);
  }

  async function search() {
    const apiClient = new PaymentApiClient();
    const payments = await apiClient.search(filterValue, page + 1, pageSize);

    setPaymentListPaged(payments);
    handleGetNewGroup();
  }

  const handleTabChange = (event: React.ChangeEvent<{}>, newIndex: number) => {
    setTabIndex(newIndex);
    handleResetDataList(new Array<TrSelectPagedList>());
  };

  const handleDownload = async () => {
    const apiClient = new PaymentApiClient();
    await apiClient.searchExport(filterValue).then(function () {
      search();
    });
  };

  const handleFilterChange = (
    e: React.ChangeEvent<{
      name?: string | undefined;
      value: any;
    }>
  ) => {
    const { name, value } = e.target;

    if (name) {
      if (nameof<typeof filterValue>("paymentGroupIds") != name) {
        setFilterValue({
          ...filterValue,
          [name]: value,
          paymentGroupIds: new Array<number>(),
        });
      } else {
        setFilterValue({ ...filterValue, [name]: value });
      }
    }

    handleResetDataList(new Array<TrSelectPagedList>());
  };

  const handleStatusFilterChange = (status: PaymentRequestStatus) => (
    e: React.ChangeEvent<HTMLInputElement>,
    checked: boolean
  ) => {
    handleResetDataList(new Array<TrSelectPagedList>());
    if (checked) {
      setFilterValue({ ...filterValue, paymentRequestStatusId: status });
    } else {
      setFilterValue({ ...filterValue, paymentRequestStatusId: undefined });
    }
  };

  const getYearMonths = () => {
    let selectedYear: YearFilter | undefined;
    if (filterValue.schoolYearId && filters.schoolYears) {
      selectedYear = filters.schoolYears.find(
        (s) => s.id === filterValue.schoolYearId
      );
    }

    return (selectedYear && [...selectedYear.months]) || [];
  };

  const resolveTabSchoolType = () => {
    const schoolTypeId =
      tabIndex == TabIndex.ElementarySchool
        ? SchoolType.Elementary
        : SchoolType.High;

    return schoolTypeId;
  };

  const tableHeadMetadata: TrTableHeadMetadata<PaymentList>[] = [
    {
      id: "orderNumber",
      label: "Redni broj zahtjeva",
      dataName: "orderNumber",
    },
    {
      id: "schoolCode",
      label: "Šifra škole",
      dataName: "schoolCode",
    },
    {
      id: "arbitraryText",
      label: "Proizvoljan tekst",
      dataName: "arbitraryText",
    },
    {
      id: "month",
      label: "Mjesec",
      dataName: "month",
    },
    {
      id: "paymentRequestId",
      label: "ID zahtjeva",
      dataName: "paymentRequestId",
    },
    {
      id: "paymentRequestStatusName",
      label: "Status",
      dataName: "paymentRequestStatusName",
      dataFunc: (payment: PaymentList) => (
        <PaymentRequestStatusElement
          statusId={payment.paymentRequestStatusId}
          statusName={payment.paymentRequestStatusName}
        />
      ),
    },
    {
      id: "paymentGroupCode",
      label: "ID grupe",
      dataName: "paymentGroupCode",
    },
    {
      id: "amount",
      label: "Iznos",
      dataFunc: (payment: PaymentList) => currencyFormat(payment.amount, payment.currency),
    },
  ];

  const tableBodyData: TrTableBodyData = {
    idDataName: "id",
    data: paymentListPaged.data,
  };

  const disabledIds: string[] =
    paymentListPaged != null &&
    paymentListPaged.data != null &&
    paymentListPaged.data.length > 0
      ? paymentListPaged.data
          .filter((p) => !p.isEnabled)
          .map((s) => s.id.toString())
      : [];

  const handleStatusChange = async () => {
    var selectedIds = getAllSelectedListItems();
    if (selectedIds && selectedIds.length > 0) {
      const apiClient = new PaymentApiClient();
      await apiClient
        .updateStatuses(
          filterValue.schoolTypeId,
          filterValue.date! as Date,
          selectedIds
        )
        .then(function (response) {
          openSnackbar("Uspješno ste izmijenili statuse zahtjeva.", "success");
        });

      handleResetDataList(new Array<TrSelectPagedList>());
      handleGetGroups(filterValue.schoolTypeId, filterValue.date! as Date);
      search();
    }
  };

  async function handleGetNewGroup() {
    const apiClient = new PaymentApiClient();
    const newGroup = await apiClient.getNewGroup(
      filterValue.schoolTypeId,
      filterValue.date! as Date
    );

    setPaymentGroup(newGroup);
  }

  return (
    <div className={classes.root}>
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <Typography variant="h5">Pregled liste za isplatu</Typography>
        </Grid>
        <Grid item xs={12}>
          <Tabs value={tabIndex} onChange={handleTabChange}>
            <Tab label="Osnovna škola" />
            <Tab label="Srednja škola" />
          </Tabs>
        </Grid>
        {filterValue && Object.keys(filterValue).length > 0 ? (
          <>
            <Grid item xs={12} md={3}>
              <TrSelect
                label={"Šk. godina"}
                value={filterValue.schoolYearId}
                name={nameof<typeof filterValue>("schoolYearId")}
                fullWidth={true}
                selectData={
                  {
                    valueProp: "id",
                    textProp: "name",
                    data: filters.schoolYears || [],
                  } as TrSelectData<YearFilter>
                }
                onChange={(e) => handleFilterChange(e)}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <TrSelect
                label={"Mjesec"}
                value={filterValue.date}
                name={nameof<typeof filterValue>("date")}
                fullWidth={true}
                selectData={
                  {
                    valueProp: "startingDate",
                    textProp: "name",
                    data: getYearMonths() || [],
                  } as TrSelectData<MonthFilter>
                }
                onChange={(e) => handleFilterChange(e)}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <TrSelect
                label={"ID grupe"}
                value={filterValue.paymentGroupIds}
                name={nameof<typeof filterValue>("paymentGroupIds")}
                fullWidth={true}
                multiple={true}
                selectData={
                  {
                    valueProp: "id",
                    textProp: "code",
                    data: groups || [],
                  } as TrSelectData<PaymentGroup>
                }
                onChange={(e) => handleFilterChange(e)}
              />
            </Grid>
            <Grid container item spacing={4} xs={12}>
              <Grid item xs={12} md={12}>
                <FormControl component="fieldset">
                  <FormGroup aria-label="position" row>
                    <FormControlLabel
                      control={
                        <Checkbox
                          color="primary"
                          checked={
                            filterValue.paymentRequestStatusId ==
                            PaymentRequestStatus.SentForPayment
                          }
                          onChange={handleStatusFilterChange(
                            PaymentRequestStatus.SentForPayment
                          )}
                          name="ChbSentForPayment"
                        />
                      }
                      label='Prikaži sve zahtjeve koji imaju status "Poslan na plaćanje"'
                    />
                    <FormControlLabel
                      control={
                        <Checkbox
                          color="primary"
                          checked={
                            filterValue.paymentRequestStatusId ==
                            PaymentRequestStatus.Paid
                          }
                          onChange={handleStatusFilterChange(
                            PaymentRequestStatus.Paid
                          )}
                          name="ChbPaid"
                        />
                      }
                      label='Prikaži sve zahtjeve koji imaju status "Plaćen"'
                    />
                  </FormGroup>
                </FormControl>
              </Grid>
            </Grid>
          </>
        ) : (
          <></>
        )}

        <Grid item xs={12}>
          <Divider />
        </Grid>
        <Grid item xs={12}>
          <Paper>
            <TrTable
              hover={true}
              headMetadata={tableHeadMetadata}
              bodyData={tableBodyData}
              enablePagination={true}
              enableSelect={true}
              enableAllSelect={true}
              disabledIds={disabledIds}
              page={page}
              pageSize={pageSize}
              totalCount={paymentListPaged.count}
              onChangePage={handleChangePage}
              onChangePageSize={handleChangepageSize}
              noDataMessage={"Trenutno nema liste za isplatu"}
              handleTableSelectChange={handleTableSelectChange}
              handleTableAllSelectChange={handleTableAllSelectChange}
              getAllSelectedListItems={getAllSelectedListItems}
              allItemsSelected={allItemsSelected}
            />
          </Paper>
        </Grid>
        <Grid item xs={12} sm={6}>
          <Typography variant="h6">
            {paymentListPaged.totalAmount
              ? `Ukupno: ${currencyFormat(paymentListPaged.totalAmount, paymentListPaged.currency)}`
              : null}
          </Typography>
        </Grid>
        <Grid item xs={12} sm={6} className={classes.downloadButton}>
          <Button
            variant="contained"
            color="primary"
            onClick={() => handleDownload()}
          >
            Preuzmi izvještaj
          </Button>
        </Grid>
      </Grid>
      <Drawer
        anchor="bottom"
        open={getAllSelectedLength && getAllSelectedLength() > 0}
        variant="persistent"
        classes={{ paper: classes.changeStatusDrawer }}
        PaperProps={{
          elevation: 2,
        }}
      >
        <Grid container spacing={2}>
          <Grid item md={3}>
            <Typography
              variant="h6"
              color="textSecondary"
              className={classes.numSelectedText}
            >
              {"Odabran broj zahtjeva: " + getAllSelectedLength().toString()}
            </Typography>
          </Grid>
          <Grid item xs={12} md={6}>
            <div className={classes.changeStatusBtn}>
              <ButtonConfirmDialog
                variant="text"
                color="default"
                btnText={
                  getAllSelectedLength() > 1
                    ? "PROMIJENI STATUS ODABRANIM ZAHTJEVIMA"
                    : "PROMIJENI STATUS ODABRANOM ZAHTJEVU"
                }
                btnIcon={<DoneIcon className={classes.editIcon} />}
                title={"Promjena statusa"}
                message={
                  "Potvrdom ove akcije promijenit ćete status odabranih zahtjeva te ih dodati u grupu " +
                  paymentGroup +
                  " bez mogućnosti kasnijeg odustajanja. Želite li nastaviti?"
                }
                cancelBtnText={"Odustani"}
                okBtnText={"Prihvati"}
                onOkClick={handleStatusChange}
              />
            </div>
          </Grid>
        </Grid>
      </Drawer>
      <TrSnackbar
        variant={snackbarVariant}
        isOpened={isSnackbarOpened}
        message={snackbarMessage}
        onClose={closeSnackbar}
      />
    </div>
  );
}

const useStyles = makeStyles((theme) => ({
  root: {
    flexGrow: 1,
  },
  downloadButton: {
    textAlign: "right",
  },
  changeStatusDrawer: {
    backgroundColor: "white",
    padding: theme.spacing(2),
  },
  numSelectedText: {
    fontSize: "1rem",
    padding: "6px 0px 6px 0px",
  },
  changeStatusBtn: {
    display: "flex",
    flexWrap: "wrap",
    justifyContent: "center",
  },
  editIcon: {
    marginRight: theme.spacing(1),
  },
}));

enum TabIndex {
  ElementarySchool = 0,
  HighSchool = 1,
}
